import { useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FileText, AlertTriangle } from 'lucide-react';
import Textarea from "@/components/forms/textarea";
import RadioGroup from "@/components/forms/radio-group";
import SimpleDatePicker from "@/components/forms/simple-date-picker";
import PostalCodeCity from "@/components/forms/postal-code-city";
import Text from "@/components/forms/text";
import { getCategoryByTypeAccident } from "@/pages/accident/declaration-sinistre/declaration/get-category-by-type-accident";
import Select from "@/components/forms/select";
import TimePicker from "@/components/forms/time-picker";
import { listePays } from "@/pages/accident/declaration-sinistre/declaration/liste-pays";
import { useTiersAccidentsData } from '@/hooks/api/use-accidents';
import { useSelectedContract } from '@/hooks/api/use-contracts';
import { isMAEContract, isWakamContract } from '@/lib/contracts';
import { formatDate } from '@/lib/dates';
import type { DeclarationStepProps } from '@/pages/accident/declaration-sinistre/types';
import { useDeclarationGaranties } from '@/pages/accident/declaration-sinistre/use-declaration-garanties';

interface MaeOption {
    value: string;
    label: string;
}

interface DeclarationProps extends DeclarationStepProps {
    setFirstCheck?: (value: boolean) => void;
}

const Declaration = (props: DeclarationProps) => {
    const { errors, values, setValue, handleChange } = props;
    const contract = useSelectedContract();
    const sinistres = useTiersAccidentsData();
    const risque = contract?.adhesionPrincipale;
    const conduct = contract?.flotte ? "" : (contract?.souscripteur || {}).nom;
    const isWakam = isWakamContract(contract);
    const isMae = isMAEContract(contract);
    const garanties = useDeclarationGaranties();

    useEffect(() => {
        setValue('risque', String(risque));
        setValue('conduct', conduct);
    }, []);

    useEffect(() => {
        let desc = '';
        if (values.equipementsDommageGants === 'T') desc += 'Gants - ';
        if (values.equipementsDommageBottes === 'T') desc += 'Bottes - ';
        if (values.equipementsDommageBlouson === 'T') desc += 'Blouson - ';
        if (values.equipementsDommageCasque === 'T') desc += 'Casque - ';
        if (values.equipementsDommageAirbag === 'T') desc += 'Airbag - ';
        if (values.equipementsDommageAutre === 'T') desc += 'Autres equipements (' + values.equipementsDommageAutreDesc + ')';
        setValue('dommagesDeclares', desc);
    }, [values.equipementsDommageGants, values.equipementsDommageBottes, values.equipementsDommageBlouson, values.equipementsDommageCasque, values.equipementsDommageAirbag, values.equipementsDommageAutre]);

    const sinistreDejaDeclare = sinistres.find(s => {
        return formatDate(s.dateSurv) === formatDate(values.datesurvorigin);
    });

    return (
        <Card className="mt-6">
            <CardHeader className="pb-6 border-b">
                <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    Votre déclaration
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-8">
                <Select
                    className="categorieSin"
                    label="Sélectionnez la catégorie du sinistre"
                    name="catsinorigin"
                    error={errors.catsinorigin}
                    value={values.catsinorigin}
                    onChange={(e) => setValue('catsinorigin', e.target.value)}
                >
                    <option value="">Choisissez la catégorie du sinistre</option>
                    {!isMae && getCategoryByTypeAccident(values.type_sinistre).map((cat, index) => (
                        <option key={index} value={cat.value}>{cat.label}</option>
                    ))}
                    {isMae && (() => {
                        const maeOptionsByType: Record<string, MaeOption[]> = {
                            accident: [
                                { value: '104.2', label: 'Un accident seul' },
                                { value: '103.1', label: 'Un accident avec un véhicule' },
                                { value: '103.3', label: 'Un accident avec un piéton/vélo' },
                                { value: '104.4', label: 'Un choc avec un animal' },
                            ],
                            vol: [
                                { value: '10.0', label: 'Vol' },
                                { value: '105.1', label: 'Tentative de vol' },
                            ],
                            autres: [
                                { value: '28.0', label: 'Catastrophes naturelles' },
                                { value: '29.0', label: 'Catastrophes technologiques' },
                            ],
                        };
                        const opts = maeOptionsByType[values.type_sinistre as string] ?? [];
                        return opts.map((opt, i) => (
                            <option key={`mae-${opt.value}-${i}`} value={opt.value}>{opt.label}</option>
                        ));
                    })()}
                </Select>

                {isMae && !garanties?.find((g) => g.code === 'MBPACB' || g.code === 'MBPACB1') && values.catsinorigin === '104.2' && (
                    <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            <strong>ATTENTION</strong> : Vous avez sélectionné une catégorie pour ce sinistre alors que votre contrat ne comprend pas la prise en charge de cette garantie. Vous ne pourrez donc pas poursuivre la déclaration.
                        </AlertDescription>
                    </Alert>
                )}

                {!isMae && !garanties?.find((g) => g.code_famille === 'DTA') && ['104.2', '104.3', '104.5'].includes(values.catsinorigin) && (
                    <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            <strong>ATTENTION</strong> : Vous avez sélectionné une catégorie Dommage tous accidents pour ce sinistre alors que votre contrat ne comprend pas la prise en charge de cette garantie. Vous ne pourrez donc pas poursuivre la déclaration.
                        </AlertDescription>
                    </Alert>
                )}

                {!isMae && !garanties?.find((g) => g.code_famille === 'VI') && ['10.0', '105.1', '9.0', '105.2', '18.0', '19.0'].includes(values.catsinorigin) && (
                    <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            <strong>ATTENTION</strong> : Vous avez sélectionné une catégorie "Vol" ou "Incendie" pour ce sinistre alors que votre contrat ne comprend pas la prise en charge de cette garantie. Vous ne pourrez donc pas poursuivre la déclaration.
                        </AlertDescription>
                    </Alert>
                )}

                {!isMae && ['103.1', '103.2', '103.3', '104.1', '104.2', '104.3', '104.4'].includes(values.catsinorigin) && (
                    <Textarea
                        label="Quel était le motif du déplacement au moment du sinistre ?"
                        name="motif_deplacement"
                        value={values.motif_deplacement}
                        error={errors.motif_deplacement}
                        maxLength={120}
                        required
                        onChange={(e) => setValue('motif_deplacement', e.target.value.toUpperCase())}
                    />
                )}

                <Textarea
                    label="Précisez les circonstances du sinistre"
                    name="commentaire"
                    value={values.commentaire}
                    error={errors.commentaire}
                    maxLength={120}
                    required
                    onChange={(e) => setValue('commentaire', e.target.value.toUpperCase())}
                />

                {values.catsinorigin === '104.1' && (
                    <RadioGroup
                        label="Connaissez-vous l'immatriculation du véhicule en fuite ?"
                        name="indentify"
                        setValue={setValue}
                        options={[{ value: 'T', label: 'Oui' }, { value: 'F', label: 'Non' }]}
                        value={values.indentify}
                        error={errors.indentify}
                        required
                    />
                )}

                {values.catsinorigin === '104.4' && (
                    <RadioGroup
                        label="Le propriétaire de l'animal est-il identifié ?"
                        name="indentify"
                        setValue={setValue}
                        options={[{ value: 'T', label: 'Oui' }, { value: 'F', label: 'Non' }]}
                        value={values.indentify}
                        error={errors.indentify}
                        required
                    />
                )}

                {values.catsinorigin === '103.3' && (
                    <RadioGroup
                        label="Le piéton/cycliste est-il identifié ?"
                        name="indentify"
                        setValue={setValue}
                        options={[{ value: 'T', label: 'Oui' }, { value: 'F', label: 'Non' }]}
                        value={values.indentify}
                        error={errors.indentify}
                        required
                    />
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                        <SimpleDatePicker
                            label="Date du sinistre"
                            name="datesurvorigin"
                            setValue={setValue}
                            value={values.datesurvorigin}
                            error={errors.datesurvorigin}
                            required
                            maxDate={new Date()}
                        />
                        {values.type_sinistre === 'vol' && (
                            <p className="mt-2 text-xs text-muted-foreground">
                                Indiquez le jour de la disparition de votre véhicule. Si vous ne connaissez pas le jour exact, indiquez la date la plus ancienne inscrite sur le procès verbal.
                            </p>
                        )}
                    </div>
                    <TimePicker
                        name="heuresurvorigin"
                        label="Heure du sinistre"
                        required
                        error={errors.heuresurvorigin}
                    />
                </div>

                <RadioGroup
                    label="Le sinistre a-t-il eu lieu dans un pays étranger ?"
                    name="pays_lieu_sinistre"
                    setValue={setValue}
                    options={[{ value: 'T', label: 'Oui' }, { value: 'F', label: 'Non' }]}
                    value={values.pays_lieu_sinistre}
                    error={errors.pays_lieu_sinistre}
                    required
                />

                {values.pays_lieu_sinistre === 'T' && (
                    <Select
                        label="Pays où le sinistre a eu lieu"
                        name="pays"
                        error={errors.pays}
                        value={values.pays}
                        onChange={(e) => {
                            setValue('pays', e.target.value);
                            setValue('lieu', e.target.value);
                            setValue('cplieu', '99000');
                        }}
                    >
                        <option value="">Choisissez un pays</option>
                        {listePays.map((pays, index) => (
                            <option key={index} value={pays.value}>{pays.label}</option>
                        ))}
                    </Select>
                )}

                {values.pays_lieu_sinistre === 'F' && (
                    <PostalCodeCity
                        label="Code postal et ville du sinistre"
                        cp="cplieu"
                        ville="lieu"
                        error={errors.lieu}
                        required
                    />
                )}

                <RadioGroup
                    label="Le conducteur est-il blessé ?"
                    name="corpoconduct"
                    setValue={setValue}
                    options={[{ value: 'T', label: 'Oui' }, { value: 'F', label: 'Non' }]}
                    value={values.corpoconduct}
                    error={errors.corpoconduct}
                    required
                />

                {isWakam && values.catsinorigin !== '10.0' && (
                    <SimpleDatePicker
                        label="Date de naissance du conducteur"
                        name="birthdate_conduct"
                        setValue={setValue}
                        value={values.birthdate_conduct}
                        error={errors.birthdate_conduct}
                        required
                    />
                )}

                {['103.1', '103.2', '103.3', '104.1', '104.2', '104.3', '104.4'].includes(values.catsinorigin) && (
                    <Text
                        name="conduct"
                        label="Nom et prénom du conducteur lors de l'accident"
                        onChange={handleChange}
                        value={values.conduct}
                        normalize={v => v.toUpperCase()}
                    />
                )}

                {(values.catsinorigin === '103.1' || values.catsinorigin === '103.2' || values.catsinorigin === '104.1' || values.catsinorigin === '104.2' || values.catsinorigin === '103.3' || values.catsinorigin === '104.4') && (
                    <RadioGroup
                        label="Y-a-t-il des témoins ?"
                        name="temoin"
                        setValue={setValue}
                        options={[{ value: 'T', label: 'Oui' }, { value: 'F', label: 'Non' }]}
                        value={values.temoin}
                        error={errors.temoin}
                        required
                    />
                )}

                {isWakam && values.catsinorigin === '103.2' && (
                    <Select
                        className="categorieSin"
                        label="Combien de véhicules sont impliqués sur ce sinistre ?"
                        name="nb_vehicules"
                        error={errors.nb_vehicules}
                        value={values.nb_vehicules}
                        onChange={(e) => setValue('nb_vehicules', e.target.value)}
                    >
                        <option value={undefined}>Sélectionnez le nombre</option>
                        {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                            <option key={n} value={String(n)}>{n}</option>
                        ))}
                    </Select>
                )}

                {values.catsinorigin !== '10.0' && (
                    <RadioGroup
                        label="Le véhicule a-t-il subi des dommages ?"
                        name="vehiculeDommage"
                        setValue={setValue}
                        options={[{ value: 'T', label: 'Oui' }, { value: 'F', label: 'Non' }]}
                        value={values.vehiculeDommage}
                        error={errors.vehiculeDommage}
                        required
                    />
                )}

                {/* Ancienne écriture `[('103.1', …, '104.4')]` : l'opérateur virgule ne gardait que '104.4' */}
                {!isMae && ['104.4'].includes(values.catsinorigin) && (
                    <RadioGroup
                        label="Il y a-t-il des dégâts sur vos équipements et accessoires ?"
                        name="equipementsDommage"
                        setValue={setValue}
                        options={[{ value: 'T', label: 'Oui' }, { value: 'F', label: 'Non' }]}
                        value={values.equipementsDommage}
                        error={errors.equipementsDommage}
                        required
                    />
                )}

                {values.equipementsDommage === 'T' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        <RadioGroup label="Votre casque" name="equipementsDommageCasque" setValue={setValue} options={[{ value: 'T', label: 'Oui' }, { value: 'F', label: 'Non' }]} value={values.equipementsDommageCasque} error={errors.equipementsDommageCasque} required />
                        <RadioGroup label="Vos gants" name="equipementsDommageGants" setValue={setValue} options={[{ value: 'T', label: 'Oui' }, { value: 'F', label: 'Non' }]} value={values.equipementsDommageGants} error={errors.equipementsDommageGants} required />
                        <RadioGroup label="Votre blouson" name="equipementsDommageBlouson" setValue={setValue} options={[{ value: 'T', label: 'Oui' }, { value: 'F', label: 'Non' }]} value={values.equipementsDommageBlouson} error={errors.equipementsDommageBlouson} required />
                        <RadioGroup label="Vos bottes" name="equipementsDommageBottes" setValue={setValue} options={[{ value: 'T', label: 'Oui' }, { value: 'F', label: 'Non' }]} value={values.equipementsDommageBottes} error={errors.equipementsDommageBottes} required />
                        <RadioGroup label="Votre gilet Airbag" name="equipementsDommageAirbag" setValue={setValue} options={[{ value: 'T', label: 'Oui' }, { value: 'F', label: 'Non' }]} value={values.equipementsDommageAirbag} error={errors.equipementsDommageAirbag} required />
                        <RadioGroup label="Un autre équipement ?" name="equipementsDommageAutre" setValue={setValue} options={[{ value: 'T', label: 'Oui' }, { value: 'F', label: 'Non' }]} value={values.equipementsDommageAutre} error={errors.equipementsDommageAutre} required />
                        {values.equipementsDommageAutre === 'T' && (
                            <Text name="equipementsDommageAutreDesc" label="Préciser l'equipement endommagé" value={values.equipementsDommageAutreDesc} onChange={handleChange} normalize={v => v.toUpperCase()} />
                        )}
                    </div>
                )}

                {sinistreDejaDeclare && (
                    <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            <strong>ATTENTION</strong> : un sinistre a déjà été déclaré à cette date, vous ne pouvez pas poursuivre la déclaration.
                        </AlertDescription>
                    </Alert>
                )}
            </CardContent>
        </Card>
    );
};

export default Declaration;
